import { useQuery } from "@tanstack/react-query";
import {
  buildPermissionsRequest,
  buildRoundPermissionsRequest,
  buildTitlePermissionsRequest,
  buildTitlePermissionsWithStateRequest,
  type Permissions,
  transformPermissionsResponse,
} from "@/lib/auth/permissions";
import { v2AuthzCheck } from "@/usersAndProjects-client";

/**
 * Result type for usePermissions hook
 */
export type UsePermissionsResult = {
  /** Permissions map (action -> boolean), undefined while loading */
  permissions: Permissions | undefined;
  /** Whether the permissions are currently being fetched */
  isLoading: boolean;
  /** Error if the fetch failed */
  error: Error | null;
  /** Function to manually refetch permissions */
  refetch: () => void;
};

/**
 * Hook to fetch global permissions (overview page, create page).
 * Requests GLOBAL_ACTIONS permissions.
 * Permissions are always fetched fresh (no caching).
 *
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true.
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * const { permissions, isLoading } = useGlobalPermissions();
 * if (permissions?.access_overview) {
 *   // User can access proofing flow
 * }
 */
export function useGlobalPermissions(options?: { enabled?: boolean }): UsePermissionsResult {
  const queryKey = ["permissions", "global"];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = buildPermissionsRequest();

      const response = await v2AuthzCheck({
        body: requestBody,
      });

      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch title-specific permissions (proofing title side sheet).
 * Requests TITLE_ACTIONS permissions (access_side_sheet, review_pdf, close_proofing_round, archive, etc).
 * Permissions are always fetched fresh (no caching).
 *
 * Supports state-dependent permissions by optionally accepting titleState parameter.
 * When titleState is provided, the Cerbos policy can evaluate state-based conditions
 * (e.g., archive action requires state='completed').
 *
 * @param projectId - Project ID (required) - the projectId that the proofing title is linked to
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true.
 * @param options.titleState - Optional title state for state-dependent permissions (e.g., "completed")
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * // Without state (general title permissions)
 * const { permissions, isLoading } = useTitlePermissions(projectId);
 * if (permissions?.edit_metadata) {
 *   // User can edit metadata for this proofing title
 * }
 *
 * @example
 * // With state (for state-dependent permissions like archive)
 * const { permissions } = useTitlePermissions(projectId, { titleState: "completed" });
 * if (permissions?.archive) {
 *   // User can archive this completed title
 * }
 */
export function useTitlePermissions(
  projectId: string,
  options?: { enabled?: boolean; titleState?: string },
): UsePermissionsResult {
  const titleState = options?.titleState;

  // Include state in query key if provided to ensure proper cache separation
  const queryKey = titleState
    ? ["permissions", "title", projectId, "state", titleState]
    : ["permissions", "title", projectId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Use state-aware request if titleState is provided
      const requestBody = titleState
        ? buildTitlePermissionsWithStateRequest(projectId, titleState)
        : buildTitlePermissionsRequest(projectId);

      const response = await v2AuthzCheck({
        body: requestBody,
      });

      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * @deprecated Use useTitlePermissions instead. Name changed to better reflect the domain model.
 */
export function useProjectPermissions(
  projectId: string,
  options?: { enabled?: boolean; titleState?: string },
): UsePermissionsResult {
  return useTitlePermissions(projectId, options);
}

/**
 * Hook to fetch round-specific permissions (document review page).
 * Requests ROUND_SPECIFIC_ACTIONS permissions (create_annotations, etc).
 * Permissions are always fetched fresh (no caching).
 *
 * @param projectId - Project ID (required)
 * @param roundState - Current round state (required, e.g., "reviewing", "approving")
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true.
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * const { permissions } = useRoundPermissions(projectId, round.state);
 * if (permissions?.create_annotations) {
 *   // User can create annotations in this round
 * }
 */
export function useRoundPermissions(
  projectId: string,
  roundState: string,
  options?: { enabled?: boolean },
): UsePermissionsResult {
  const queryKey = ["permissions", "project", projectId, "round", roundState];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = buildRoundPermissionsRequest(projectId, roundState);

      const response = await v2AuthzCheck({
        body: requestBody,
      });

      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * @deprecated Use useGlobalPermissions, useProjectPermissions (with titleState option), or useRoundPermissions instead
 */
export function usePermissions(
  projectId?: string,
  options?: { enabled?: boolean; roundState?: string; titleState?: string },
): UsePermissionsResult {
  // Build cache key based on projectId, roundState, and titleState
  const queryKey = projectId
    ? options?.roundState
      ? ["permissions", "project", projectId, "round", options.roundState]
      : options?.titleState
        ? ["permissions", "project", projectId, "state", options.titleState]
        : ["permissions", "project", projectId]
    : ["permissions", "global"];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Build the permissions request using the appropriate utility
      const requestBody =
        options?.roundState && projectId
          ? buildRoundPermissionsRequest(projectId, options.roundState)
          : options?.titleState && projectId
            ? buildTitlePermissionsWithStateRequest(projectId, options.titleState)
            : projectId
              ? buildTitlePermissionsRequest(projectId)
              : buildPermissionsRequest();

      // Use the generated client instead of manual fetch
      const response = await v2AuthzCheck({
        body: requestBody,
      });

      // Transform the response to a simple boolean map
      // The generated client wraps the response in a 'data' property
      // Cast to PermissionsResponse since the generated types have optional fields
      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true, // Allow disabling the query
    staleTime: 0, // Always consider data stale - fetch fresh every time
    gcTime: 0, // Don't cache data
    retry: 1, // Retry failed requests once
    refetchOnWindowFocus: false, // Don't refetch on tab focus
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
